// Persistent product cache backed by localStorage with TTL + version busting.
// Falls back to in-memory only when localStorage is unavailable (Safari quota, etc.)

import type { ShopifyProductJSON } from '@/scripts/core/product-resolver'
import type { Cart } from '@/scripts/shopify'
import { initCartState } from '@/scripts/cart/cart-state'

type CacheEntry = {
	version: string
	ts: number
	data: Record<string, ShopifyProductJSON>
}

type IndexEntry = {
	version: string
	ts: number
	/** product ID → handle */
	idMap: Record<string, string>
	/** variant ID → handle */
	variantMap: Record<string, string>
}

const STORAGE_KEY_PRODUCTS = 'pv:products'
const STORAGE_KEY_INDEX = 'pv:product-index'
const STORAGE_KEY_CART = 'pv:cart-snapshot'
const TTL_MS = 30 * 60 * 1000 // 30 minutes
export const CART_SNAPSHOT_FRESH_MS = 15 * 1000

// In-memory mirrors
const productMap = new Map<string, ShopifyProductJSON>()
const idToHandle = new Map<number, string>()
const variantToHandle = new Map<number, string>()

let storageAvailable = true

function getVersion(): string {
	return (window as any).__themeVersion ?? '0'
}

function readStorage<T>(key: string): T | null {
	if (!storageAvailable) return null
	try {
		const raw = localStorage.getItem(key)
		if (!raw) return null
		return JSON.parse(raw) as T
	} catch {
		return null
	}
}

function writeStorage(key: string, value: unknown): void {
	if (!storageAvailable) return
	try {
		localStorage.setItem(key, JSON.stringify(value))
	} catch {
		// Quota exceeded — disable storage writes for this session
		storageAvailable = false
	}
}

let flushTimer: ReturnType<typeof setTimeout> | null = null

function scheduleFlush(): void {
	if (flushTimer) return
	flushTimer = setTimeout(() => {
		flushTimer = null
		flushToStorage()
	}, 1000)
}

function flushToStorage(): void {
	const version = getVersion()
	const ts = Date.now()

	const data: Record<string, ShopifyProductJSON> = {}
	for (const [handle, product] of productMap) {
		data[handle] = product
	}

	const idMap: Record<string, string> = {}
	for (const [id, handle] of idToHandle) {
		idMap[String(id)] = handle
	}

	const variantMap: Record<string, string> = {}
	for (const [vid, handle] of variantToHandle) {
		variantMap[String(vid)] = handle
	}

	writeStorage(STORAGE_KEY_PRODUCTS, {
		version,
		ts,
		data,
	} satisfies CacheEntry)
	writeStorage(STORAGE_KEY_INDEX, {
		version,
		ts,
		idMap,
		variantMap,
	} satisfies IndexEntry)
}

function indexProduct(product: ShopifyProductJSON): void {
	idToHandle.set(product.id, product.handle)
	for (const v of product.variants) {
		variantToHandle.set(v.id, product.handle)
	}
}

/** Load localStorage into memory, bust if stale or version-mismatched. */
export function initProductCache(): void {
	const version = getVersion()

	// Test localStorage availability
	try {
		localStorage.setItem('pv:test', '1')
		localStorage.removeItem('pv:test')
	} catch {
		storageAvailable = false
	}

	const entry = readStorage<CacheEntry>(STORAGE_KEY_PRODUCTS)
	const index = readStorage<IndexEntry>(STORAGE_KEY_INDEX)

	const now = Date.now()
	const isStale =
		!entry || entry.version !== version || now - entry.ts > TTL_MS

	if (isStale) {
		// Clear stale data
		if (storageAvailable) {
			try {
				localStorage.removeItem(STORAGE_KEY_PRODUCTS)
				localStorage.removeItem(STORAGE_KEY_INDEX)
				localStorage.removeItem(STORAGE_KEY_CART)
			} catch {
				/* ignore */
			}
		}
	} else {
		// Hydrate in-memory from localStorage
		for (const [handle, product] of Object.entries(entry.data)) {
			productMap.set(handle, product)
			indexProduct(product)
		}
	}

	// Also hydrate index if it survived separately
	if (
		index &&
		index.version === version &&
		now - index.ts <= TTL_MS &&
		isStale === false
	) {
		for (const [id, handle] of Object.entries(index.idMap)) {
			idToHandle.set(Number(id), handle)
		}
		for (const [vid, handle] of Object.entries(index.variantMap)) {
			variantToHandle.set(Number(vid), handle)
		}
	}

	// Seed from PDP product JSON if available
	warmFromSeedElement()

	// Hydrate last-known cart immediately (stale-while-revalidate).
	hydrateCartSnapshotFromStorage()

	// Seed offer rules for optimistic GWP/membership prediction
	warmOfferRules()

	// Pre-fetch cart state for synchronous membership checks
	warmCartSnapshot()
}

/** Parse inline product-seed script tags emitted by Liquid and populate the cache.
 *  - `script[data-pv-product-seed]` — rendered by snippets/product-cache-seed.liquid
 *    for every product in the membership-product-data + bundle-product-data collections.
 *  - `#pv-product-seed` — rendered by layout/theme.liquid on PDP pages with the richer
 *    product-page JSON (may include selling_plan_allocations etc.). Parsed LAST so it
 *    overwrites any slimmer collection-context version of the same product. */
function warmFromSeedElement(): void {
	// Collection-level seeds first.
	const seedEls = document.querySelectorAll<HTMLScriptElement>(
		'script[data-pv-product-seed]'
	)
	for (const el of seedEls) {
		const text = el.textContent
		if (!text) continue
		try {
			const product = JSON.parse(text) as ShopifyProductJSON
			if (product?.handle) cacheProduct(product)
		} catch {
			/* malformed JSON — skip */
		}
	}

	// PDP seed LAST so its richer context wins for the current product.
	const pdpEl = document.getElementById('pv-product-seed')
	if (!pdpEl?.textContent) return
	try {
		const product = JSON.parse(pdpEl.textContent) as ShopifyProductJSON
		if (product?.handle) cacheProduct(product)
	} catch {
		/* malformed JSON — skip */
	}
}

/** Add a product to the cache (in-memory + debounced localStorage flush). */
export function cacheProduct(product: ShopifyProductJSON): void {
	productMap.set(product.handle, product)
	indexProduct(product)
	scheduleFlush()
}

/** Lookup by handle. */
export function getCachedProduct(
	handle: string
): ShopifyProductJSON | undefined {
	return productMap.get(handle)
}

/** Lookup by product ID. */
export function getCachedProductById(
	id: number
): ShopifyProductJSON | undefined {
	const handle = idToHandle.get(id)
	if (handle) return productMap.get(handle)
	// Linear scan fallback (in case index is incomplete)
	for (const product of productMap.values()) {
		if (product.id === id) return product
	}
	return undefined
}

/** Lookup by variant ID. */
export function getCachedProductByVariantId(
	variantId: number
): ShopifyProductJSON | undefined {
	const handle = variantToHandle.get(variantId)
	if (handle) return productMap.get(handle)
	// Linear scan fallback
	for (const product of productMap.values()) {
		if (product.variants.some((v) => v.id === variantId)) return product
	}
	return undefined
}

// ── Offer rules (GWP, bundle membership, ALCM) ─────────────────────

export type OfferRule = {
	type: 'gwp' | 'bundle_membership' | 'alcm'
	handle: string
	priority?: number
	triggerProductIds: number[]
	triggerExclusionsId: number[]
	displayCta: string
	startDate?: string
	endDate?: string
	giftProduct: {
		id: number
		title: string
		handle: string
		type: string
		featured_image?: string
		variantId: number
		variantTitle: string
		price: number
		sellingPlanId?: number
		sellingPlanName?: string
		isOneTimeMembership?: boolean
		membershipDuration?: string
		trialMetafields?: {
			is_trial?: string
			trial_term?: string
			trial_unit?: string
		}
	}
}

const offerRules: OfferRule[] = []
let _offerRulesResolve: (() => void) | null = null
const _offerRulesPromise = new Promise<void>((resolve) => {
	_offerRulesResolve = resolve
})

/**
 * Returns a promise that resolves once offer rules have been loaded
 * (either from the DOM or from the async warmCartSnapshot response).
 * Already resolved if rules loaded synchronously at page load.
 */
export function offerRulesReady(): Promise<void> {
	return _offerRulesPromise
}

/** Parse offer rules from a DOM element or parsed HTML document. */
function warmOfferRules(doc?: Document): void {
	const root = doc ?? document
	const el = root.getElementById('pv-offer-rules-seed')
	if (!el?.textContent) return
	try {
		const rules = JSON.parse(el.textContent) as OfferRule[]
		if (Array.isArray(rules)) {
			// Clear any stale rules before populating
			offerRules.length = 0
			offerRules.push(...rules)

			// Cache gift products so createOptimisticAdditionElements
			// can find correct titles/images at T=1.2s
			for (const rule of offerRules) {
				const gp = rule.giftProduct
				if (!gp.variantId || !gp.title) continue
				if (getCachedProductByVariantId(gp.variantId)) continue // real data wins

				cacheProduct({
					id: gp.id,
					title: gp.title,
					handle: gp.handle,
					type: gp.type,
					featured_image: gp.featured_image,
					images: gp.featured_image ? [gp.featured_image] : [],
					variants: [
						{
							id: gp.variantId,
							title: gp.variantTitle,
							price: gp.price,
							compare_at_price: null,
							available: true,
							sku: '',
						},
					],
				})
			}

			// Signal that offer rules are available
			_offerRulesResolve?.()
		}
	} catch (e) {
		console.warn('[ProductCache] Failed to parse offer rules seed:', e)
	}
}

/**
 * Given a product ID being added to cart, return the offer rules that
 * would trigger (GWP, bundle membership, ALCM). Returns empty array
 * if no rules match.
 */
export function getTriggeredOfferRules(productId: number, variantId?: number): OfferRule[] {
	const today = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
	return offerRules.filter((rule) => {
		// Nothing to do if there are no triggers.
		if (rule.triggerProductIds.length === 0) return false
		// Check trigger product match
		if (
			rule.triggerProductIds.length > 0
			&& !rule.triggerProductIds.includes(productId)
		) {
			return false
		}
		if (rule.triggerExclusionsId.includes(variantId!)) return false
		// Check date range (GWP rules have optional start/end dates)
		if (rule.startDate && today < rule.startDate) return false
		if (rule.endDate && today > rule.endDate) return false
		return true
	})
}

// ── Cart snapshot (lightweight state for synchronous checks) ─────

type CartSnapshotItem = {
	product_id: number
	properties?: Record<string, string>
	product_type?: string
	title?: string
	handle?: string
}

type CartSnapshot = {
	hasMembership: boolean
	hasBundledMembership: boolean
	hasBundleItem: boolean
	hasExclusiveItem: boolean
}

type CartSnapshotSource = 'storage' | 'network' | 'runtime'

type CartSnapshotMeta = {
	ts: number
	source: CartSnapshotSource
	isVerifiedThisPage: boolean
}

type CartStorageEntry = {
	version: string
	ts: number
	cart: Cart
}

let _cartSnapshot: CartSnapshot | null = null
let _cartSnapshotMeta: CartSnapshotMeta | null = null

/** Returns the current cart snapshot, or null if not yet loaded. */
export function getCartSnapshot(): CartSnapshot | null {
	return _cartSnapshot
}

export function getCartSnapshotMeta(): CartSnapshotMeta | null {
	return _cartSnapshotMeta
}

export function isCartSnapshotFresh(
	maxAgeMs: number = CART_SNAPSHOT_FRESH_MS
): boolean {
	if (!_cartSnapshotMeta) return false
	return Date.now() - _cartSnapshotMeta.ts <= maxAgeMs
}

export function isCartSnapshotVerifiedThisPage(): boolean {
	return Boolean(_cartSnapshotMeta?.isVerifiedThisPage)
}

/**
 * Update the cart snapshot from an array of cart items.
 * Called after every successful cart fetch or add.
 */
export function updateCartSnapshot(items: CartSnapshotItem[]): void {
	updateCartSnapshotWithMeta(items)
}

function updateCartSnapshotWithMeta(
	items: CartSnapshotItem[],
	options?: {
		source?: CartSnapshotSource
		isVerifiedThisPage?: boolean
		ts?: number
	}
): void {
	const hasStandaloneTrial = items.some((item) => {
		const properties = item.properties ?? {}
		const isTrial =
			String(properties._is_trial ?? '').toLowerCase() === 'true'
		if (!isTrial) return false
		return !(
			Boolean(properties._bundle_membership_item) ||
			Boolean(properties._alcm_item) ||
			Boolean(properties._activation_item)
		)
	})

	const hasMembership = items.some(
		(item) =>
			Boolean(item.properties?._bundle_membership_item) ||
			Boolean(item.properties?._alcm_item) ||
			Boolean(item.properties?._activation_item) ||
			(item.product_type ?? '').toLowerCase() === 'streaming' ||
			(item.product_type ?? '').toLowerCase().includes('membership') ||
			(item.title ?? '').toLowerCase().includes('membership')
	)

	const hasBundleItem = items.some(
		(item) =>
			Boolean(item.properties?._bundle_offer) ||
			Boolean(item.properties?._bundle_parent_id) ||
			(item.product_type ?? '').toLowerCase() === 'bundle' ||
			(item.handle ?? '').toLowerCase().includes('bundle') ||
			(item.title ?? '').toLowerCase().includes('bundle')
	)

	_cartSnapshot = {
		hasMembership,
		hasBundledMembership: items.some(
			(item) =>
				Boolean(item.properties?._bundle_membership_item) ||
				Boolean(item.properties?._alcm_item) ||
				Boolean(item.properties?._activation_item)
		),
		hasBundleItem,
		hasExclusiveItem: hasMembership || hasBundleItem || hasStandaloneTrial,
	}

	const source = options?.source ?? 'runtime'
	_cartSnapshotMeta = {
		ts: options?.ts ?? Date.now(),
		source,
		isVerifiedThisPage: options?.isVerifiedThisPage ?? source !== 'storage',
	}
}

function hydrateCartSnapshotFromStorage(): void {
	const version = getVersion()
	const entry = readStorage<CartStorageEntry>(STORAGE_KEY_CART)
	if (!entry) return

	const isStale = entry.version !== version || Date.now() - entry.ts > TTL_MS
	if (isStale) {
		if (storageAvailable) {
			try {
				localStorage.removeItem(STORAGE_KEY_CART)
			} catch {
				/* ignore */
			}
		}
		return
	}

	if (!entry.cart || !Array.isArray(entry.cart.items)) return

	updateCartSnapshotWithMeta(entry.cart.items, {
		source: 'storage',
		isVerifiedThisPage: false,
		ts: entry.ts,
	})
	initCartState(entry.cart)
}

export function persistCartSnapshot(cart: Cart): void {
	writeStorage(STORAGE_KEY_CART, {
		version: getVersion(),
		ts: Date.now(),
		cart,
	} satisfies CartStorageEntry)
}

/**
 * Fetch cart state + offer rules in a single request via POST /cart/update.js
 * with empty updates and the cart-update-instructions section.
 * This populates both the cart snapshot and the offer rules on page load.
 */
function warmCartSnapshot(): void {
	fetch('/cart/update.js', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({
			updates: {},
			sections: ['cart-update-instructions'],
			sections_url: '/cart',
		}),
	})
		.then((r) => r.json())
		.then((cart) => {
			if (cart && Array.isArray(cart.items)) {
				updateCartSnapshotWithMeta(cart.items, {
					source: 'network',
					isVerifiedThisPage: true,
				})
				// Hydrate CartState for optimistic cart operations
				initCartState(cart)
				persistCartSnapshot(cart)
			}
			// Parse offer rules from the section HTML
			const sectionHTML = cart?.sections?.['cart-update-instructions']
			if (sectionHTML) {
				const doc = new DOMParser().parseFromString(
					sectionHTML,
					'text/html'
				)
				warmOfferRules(doc)
			}
			// Resolve even if no rules found — means there truly are none
			_offerRulesResolve?.()
		})
		.catch((e) => {
			console.warn(
				'[ProductCache] warmCartSnapshot failed, falling back to GET /cart.js',
				e
			)
			// Resolve offer rules promise — can't get rules, don't block
			_offerRulesResolve?.()
			// Fallback: at least get the cart snapshot
			fetch('/cart.js', {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			})
				.then((r) => r.json())
				.then((cart) => {
					if (cart && Array.isArray(cart.items)) {
						updateCartSnapshotWithMeta(cart.items, {
							source: 'network',
							isVerifiedThisPage: true,
						})
						initCartState(cart)
						persistCartSnapshot(cart)
					}
				})
				.catch(() => {
					/* ignore */
				})
		})
}

/** Re-fetch cart state to keep the snapshot current after cart mutations. */
export function refreshCartSnapshot(): void {
	fetch('/cart.js', {
		method: 'GET',
		headers: { 'Content-Type': 'application/json' },
	})
		.then((r) => r.json())
		.then((cart) => {
			if (cart && Array.isArray(cart.items)) {
				updateCartSnapshotWithMeta(cart.items, {
					source: 'network',
					isVerifiedThisPage: true,
				})
				initCartState(cart)
				persistCartSnapshot(cart)
			}
		})
		.catch(() => {
			/* ignore */
		})
}
